/**
 * 放送地点マスタの一覧 + CRUD ページ。
 *
 * トップページ (線区未選択時):
 *   - 全件テーブルの代わりに `RouteLandingSelector` を表示する。
 *     実運用では「まず線区を選ぶ」のが最初の操作であり、全線区の一覧を眺める場面が無いため。
 *   - ランディングは **1 段階選択** で、線区カード内の方面ボタンを 1 クリックすると
 *     そのまま一覧 + 地図画面へ遷移する。
 *   - この状態では一覧 API (全件 scan) を呼ばない。初期表示が軽くなる。
 *
 * 全線区モード (線区ドロップダウンで「(全線区)」を選んだ状態):
 *   - `selectedRouteId === ALL_ROUTES` で表され、**未選択 (空文字) とは区別する**。
 *     以前は「(全線区)」の値が空文字だったため、選んだ瞬間に「未選択」と判定されて
 *     線区選択ランディングへ戻ってしまう不具合があった。
 *   - 一覧は routeDirection を指定せずに取得する (= 全件 scan)。
 *   - 地図も表示し、全線区の放送地点ピンを重ねて描画する。
 *     線形 (GeoJSON) は線区/方面が特定できないため描画しない。
 *
 * 線区選択後のレイアウト (画面全体を使う 2 カラム構成):
 *   - 左カラム
 *       1. 操作パネル: 線区/方面セレクタ (線区カラーのライン付き) + アクション
 *          (線区選択に戻る / 再読み込み / 新規作成 / Import/Export)
 *       2. 放送地点テーブル (残り領域をスクロール)
 *   - 右カラム: 地図 (ウィンドウ下端までフル高)
 *       - 線区が選択されているときだけ表示し、選択された路線を強調表示する。
 *       - 既存の放送地点をクリック可能なピンで描画し、クリックで編集モーダルを開く。
 *   - 2 カラムは `SplitPane` で分割し、境界バーのドラッグで配分を変更できる。
 *   - 線区未選択時は地図が無いため、左カラムのみを全幅表示する。
 *
 * 編集 / 新規作成は BroadcastPointForm モーダル内で行い、保存時には ControlPanel API
 * (POST/PUT/DELETE) を即座に呼び出して結果を再取得する。
 *
 * 選択中の線区 / 方面は `useRouteSelection` により URL クエリ + sessionStorage に
 * 保持されるため、画面をリロードしても選択状態が維持される。
 */
import { Spinner } from '../components/Spinner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ApiError, base64ToAudioUrl, BroadcastPointsClient } from '../api/broadcastPoints';
import type {
  BroadcastContentCategory,
  BroadcastPoint,
  Chime,
  FreeTextTemplate,
  HistoryRecordResult,
  SyncedText,
  PhraseTemplate,
} from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { ChimesClient } from '../api/chimes';
import { FreeTextTemplatesClient } from '../api/freeTextTemplates';
import { SyncedTextsClient } from '../api/syncedTexts';
import { PhraseTemplatesClient } from '../api/phraseTemplates';
import type { AppConfig } from '../config';
import { useHomeNavigationGuard } from '../contexts/HomeNavigationGuard';
import { PhraseTemplateProvider } from '../contexts/PhraseTemplateContext';
import { FreeTextTemplateProvider } from '../contexts/FreeTextTemplateContext';
import { SyncedTextProvider } from '../contexts/SyncedTextContext';
import { TranslateProvider } from '../contexts/TranslateContext';
import type { GtfsStop } from '../data/gtfsStops';
import { fetchIStationsLines, type IStationsLine } from '../data/istationsLines';

import {
  fetchRoutesMaster,
  getRouteColor,
  parseRouteDirection,
  toRouteDirection,
  type RouteMaster,
} from '../data/routes';
import {
  fetchRouteLine,
  fetchRouteShapePoints,
  type RouteLineFile,
  type RouteShapePointsFile,
} from '../data/routeShapes';
import { findStationSegment, sortStopsBySequence, type RouteStop } from '../data/routeStops';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { ALL_ROUTES, sanitizeSelection, useRouteSelection } from '../hooks/useRouteSelection';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { buildDefaultSsmlResolver, extractContents } from '../utils/broadcastContents';
// Issue #639: 言語別の Polly 音声設定 (voice_settings) の変換。
import {
  buildVoiceSettingsPayload,
  collectContentLanguages,
  japaneseVoiceFromSettings,
  toVoiceSelections,
} from '../utils/voiceSettings';
import { fingerprint, type RemoteChangeState } from '../utils/remoteChange';

import {
  BroadcastPointForm,
  type BroadcastPointFormValues,
} from '../components/BroadcastPointForm';
import { BroadcastPointsMap } from '../components/BroadcastPointsMap';
import { BroadcastPointsTable, getPointKey } from '../components/BroadcastPointsTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CopyDialog } from '../components/CopyDialog';
import { ImportExportControl } from '../components/ImportExportControl';
import { RouteLandingSelector } from '../components/RouteLandingSelector';
import { RouteSelector } from '../components/RouteSelector';
import { SplitPane } from '../components/SplitPane';

interface BroadcastPointsPageProps {
  config: AppConfig;
}

interface FormState {
  mode: 'create' | 'edit';
  initialValue: BroadcastPointFormValues;
}

const DEFAULT_RADIUS = 100;
const DEFAULT_BROADCAST_DELAY = 0;

/** 変更通知メールのリンクで、編集モードで開く放送地点 (spotId) を渡す URL クエリのキー (Issue #806) */
export const EDIT_QUERY_KEY = 'edit';

type SortOrder = 'station_order' | 'created_at';
type ContentFilterValue = '' | BroadcastContentCategory;

export function BroadcastPointsPage(props: BroadcastPointsPageProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('broadcast-point-history-warning');

  const apiClient = useMemo(
    () => new BroadcastPointsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /** チャイム音マスタ (`/chimes`) API クライアント (要件 3・4) */
  const chimesClient = useMemo(
    () => new ChimesClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /**
   * 定型文マスタ (`/phrase-templates`) API クライアント (Issue #552)。
   *
   * コンテンツ編集画面の「既定文面 (薄字)」を **定型文マスタの文面** で表示するために使う。
   * 以前はコード内定数を直接薄字にしていたため、定型文マスタを編集しても
   * 「薄字 = 実際に放送される文面」という UI の説明と実挙動が食い違っていた。
   */
  const phraseTemplatesClient = useMemo(
    () => new PhraseTemplatesClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /** 任意テキストテンプレートマスタ (`/free-text-templates`) API クライアント */
  const freeTextTemplatesClient = useMemo(
    () => new FreeTextTemplatesClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /** 共有テキストマスタ (`/synced-texts`) API クライアント */
  const syncedTextsClient = useMemo(
    () => new SyncedTextsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [routes, setRoutes] = useState<RouteMaster[]>([]);
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始/終了駅ドロップダウン用。
   * routes 読み込み後に ControlPanel API (`GET /route-stops`) からまとめて事前取得する。
   */
  const [routeStops, setRouteStops] = useState<Record<string, RouteStop[]>>({});

  /**
   * アイステイションズ路線マスタ (`istations-lines.json`)。
   *
   * 接続線遅延コンテンツの「運行情報の参照元（アイステイションズ路線）」複数選択の
   * 選択肢として使う。ATOS 線区マスタ (`routes`) とは **独立した別系統** で、
   * 接続線の `route_id` (ATOS) と一致しない路線も選べる必要があるため連動させない。
   *
   * 取得失敗時は空配列のまま (= プルダウンに選択肢が出ない) とし、放送地点の
   * 編集・保存自体はブロックしない (routes-master と同様のフェイルセーフ)。
   */
  const [istationsLines, setIStationsLines] = useState<IStationsLine[]>([]);

  /**
   * チャイム音マスタ一覧 (要件 3・4)。
   * 放送地点フォームの「放送開始 / 終了チャイム」セレクタの選択肢として使う。
   * 取得失敗時は空配列のまま (= セレクタが「(なし)」のみ) とし、放送地点の
   * 編集・保存自体はブロックしない (チャイムはオプション機能のためフェイルセーフ)。
   */
  const [chimes, setChimes] = useState<Chime[]>([]);

  /**
   * 定型文マスタ一覧 (Issue #552)。
   *
   * コンテンツ編集画面の SSML 入力欄の「既定文面 (薄字 = placeholder)」と
   * 多言語欄の既定値に使う。放送側 (`ssml_builder.resolve_phrase_template`) と
   * 同じ `template_id` を引くため、「薄字 = 実際に放送される文面」が成立する。
   *
   * `include_defaults: true` で取得するのは、**未登録の種別についても
   * コード内既定文面が `body` として同梱される** ため。これにより
   * 「マスタ未登録の種別だけ薄字が消える」ことがなくなる (フェイルセーフ)。
   *
   * 取得失敗時は空配列のままにして、各入力欄をコード内既定文面 (従来の挙動) へ
   * フォールバックさせる (`istationsLines` / `chimes` と同じ方針で、
   * 定型文マスタの取得失敗が放送地点の編集・保存をブロックしないようにする)。
   */
  const [phraseTemplates, setPhraseTemplates] = useState<PhraseTemplate[]>([]);

  /**
   * 任意テキストテンプレート (`/free-text-templates`)。
   * 任意テキスト放送「放送文面 (通常テキスト)」の「登録済みテキストから選択」の候補に使う。
   * 取得失敗は放送地点の編集・保存を妨げない (選択 UI が出ないだけ)。
   */
  const [freeTextTemplates, setFreeTextTemplates] = useState<FreeTextTemplate[]>([]);
  /**
   * 共有テキストマスタの一覧。任意テキスト放送「共有テキスト」の参照先の選択と本文表示に使う。
   * 取得失敗は放送地点の編集・保存を妨げない (選択肢が出ない / 参照切れ警告が出るだけ)。
   */
  const [syncedTexts, setSyncedTexts] = useState<SyncedText[]>([]);

  /**
   * GTFS 静的 stops (親駅 + 子番線) の全件。
   *
   * 任意テキスト放送の放送条件「停車番線」(`arrival_stop_id`) のドロップダウン候補として
   * 使う。`GET /gtfs-stops` は `stops.txt` に `route_id` カラムが無いため **route_id で
   * 絞り込めず**、`routeStops` のように route_id 別 Record を組み立てられない。
   * そのため線区選択に関係なく **マウント時に 1 回だけ全件取得** してキャッシュし、
   * フォームへそのまま渡す (番線候補への絞り込みは `findPlatformStopIds` が行う)。
   *
   * 取得失敗時は空配列のまま (= 番線セレクトが無効化されて注記が出るだけ) とし、
   * 放送地点の編集・保存自体はブロックしない
   * (`istationsLines` / `chimes` と同じフェイルセーフ方針)。
   */
  const [gtfsStops, setGtfsStops] = useState<GtfsStop[]>([]);

  /**
   * 選択中の方面に対応する線形 Point GeoJSON。
   * `null` (= GeoJSON 未配信) の時は地図側で線形を描画しない。
   */
  const [routeShapePoints, setRouteShapePoints] = useState<RouteShapePointsFile | null>(null);
  /**
   * 選択中の線区の路線線形 (LineString。例: 横浜線)。地図に線区カラーで描く。
   * 方面に依存しないため route_id 単位で取得する。未配置の線区は `null`。
   */
  const [routeLine, setRouteLine] = useState<{ routeId: string; file: RouteLineFile } | null>(null);
  const [allPoints, setAllPoints] = useState<BroadcastPoint[]>([]);

  /**
   * 選択中の線区 / 方面。URL クエリ (`?route=...&direction=...`) と sessionStorage に
   * 同期されるため、画面リロードでも選択が保持される。
   */
  const { routeId, directionName, setSelection, clearSelection } = useRouteSelection();

  /**
   * マスタ検証済みの選択値。
   * URL に手打ちされた不正な線区/方面や、マスタから削除された線区を弾く。
   */
  const { routeId: selectedRouteId, directionName: selectedDirectionName } = useMemo(
    () => sanitizeSelection(routes, { routeId, directionName }),
    [routes, routeId, directionName],
  );

  /**
   * URL / sessionStorage に不正な値 (マスタに無い線区・方面) が入っていた場合は、
   * 検証済みの値で URL クエリを書き戻して整合させる。
   * (書き戻さないと、URL に不正値が残り続けてブックマーク時に再現してしまう)
   */
  useEffect(() => {
    if (routes.length === 0) return;
    if (routeId === selectedRouteId && directionName === selectedDirectionName) return;
    setSelection(selectedRouteId, selectedDirectionName);
  }, [routes, routeId, directionName, selectedRouteId, selectedDirectionName, setSelection]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('station_order');
  const [contentFilter, setContentFilter] = useState<ContentFilterValue>('');
  const [focusPoint, setFocusPoint] = useState<BroadcastPoint | null>(null);
  /**
   * 削除確認ダイアログの対象。null の場合はダイアログ非表示。
   * window.confirm の代わりにアプリ独自の ConfirmDialog を表示する。
   */
  const [deleteTarget, setDeleteTarget] = useState<BroadcastPoint | null>(null);
  /**
   * 「複製」ボタンで選ばれた複製元。null の場合はコピー先選択ダイアログ (CopyDialog) 非表示。
   */
  const [copyTarget, setCopyTarget] = useState<BroadcastPoint | null>(null);
  /**
   * 一括削除 (Issue #628) 用の選択状態。`getPointKey` (routeDirection + spotId) の集合。
   * 実際の削除対象は「現在表示中の行のうち選択済みのもの」に限定する
   * (フィルター等で非表示になった行を意図せず消さないため)。
   */
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
  /** 一括削除の確認ダイアログを表示中か */
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  /** 一括削除の実行中 (二重送信防止) */
  const [bulkDeleting, setBulkDeleting] = useState(false);
  /** 有効 / 無効の更新中の放送地点キー (`getPointKey`)。該当行のトグルを操作不可にする (Issue #679)。 */
  const [togglingKeys, setTogglingKeys] = useState<ReadonlySet<string>>(new Set());
  /** 選択地点の一括有効 / 無効の実行中 (二重送信防止 / Issue #679) */
  const [bulkTogglingEnabled, setBulkTogglingEnabled] = useState(false);
  /**
   * 一括有効 / 無効の確認ダイアログの対象。null の場合は非表示。
   * true = 一括有効、false = 一括無効 (Issue #765)。
   */
  const [bulkEnabledConfirm, setBulkEnabledConfirm] = useState<boolean | null>(null);

  /**
   * 「(全線区)」が選択されているか。
   * 未選択 (空文字) と違い、一覧 + 地図はそのまま表示し、線区での絞り込みだけを外す。
   */
  const isAllRoutes = selectedRouteId === ALL_ROUTES;

  /**
   * アイステイションズ路線マスタの初回取得。
   *
   * `routes-master.json` と同様に CloudFront 同居アセットを fetch するだけなので、
   * 線区選択の有無に関わらずマウント時に 1 回だけ取得する。
   * 取得に失敗しても画面全体のエラー表示はせず空配列のままにする
   * (接続線遅延の参照元設定は任意項目のため、他の操作をブロックしない)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const file = await fetchIStationsLines();
        if (aborted) return;
        setIStationsLines(Array.isArray(file.lines) ? file.lines : []);
      } catch (err) {
        // マスタ未配置 / 取得失敗時は選択肢なしで続行する (フェイルセーフ)。
        console.warn('Failed to load istations-lines.json', err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  const selectedRouteDirection = useMemo(() => {
    // 全線区モードは特定の routeDirection を持たない (= 全件取得)
    if (isAllRoutes || !selectedRouteId || !selectedDirectionName) return '';
    return toRouteDirection(selectedRouteId, selectedDirectionName);
  }, [isAllRoutes, selectedRouteId, selectedDirectionName]);

  // 表示対象の絞り込み (テーブル + 地図用 otherPoints)
  const filteredPoints = useMemo(() => {
    // 全線区モードでは線区・方面の絞り込みを行わず、取得した全件をそのまま使う
    if (isAllRoutes) return allPoints;
    return allPoints.filter((p) => {
      if (selectedRouteId) {
        const { routeId } = parseRouteDirection(p.routeDirection);
        if (routeId !== selectedRouteId) return false;
      }
      if (selectedDirectionName) {
        const { directionName } = parseRouteDirection(p.routeDirection);
        if (directionName !== selectedDirectionName) return false;
      }
      return true;
    });
  }, [allPoints, isAllRoutes, selectedRouteId, selectedDirectionName]);

  /**
   * filteredPoints にコンテンツ種別フィルターとソートを適用した結果。
   * テーブル表示に使用する。地図は filteredPoints をそのまま使う。
   */
  const sortedFilteredPoints = useMemo(() => {
    // 1. コンテンツ種別フィルター
    const afterFilter = contentFilter
      ? filteredPoints.filter((p) => extractContents(p).some((c) => c.category === contentFilter))
      : filteredPoints;

    // 2. ソート
    // 駅順マスタは放送地点自身の線区 (routeDirection の線区部分) から引く。
    // 全線区モードでは複数線区が混在するため、選択中の線区固定では引けない。
    const stopsOf = (point: BroadcastPoint): RouteStop[] => {
      const { routeId: pointRouteId } = parseRouteDirection(point.routeDirection);
      return routeStops[pointRouteId] ?? [];
    };

    return afterFilter.slice().sort((a, b) => {
      if (sortOrder === 'station_order') {
        // 全線区モードでは、まず線区#方面ごとにまとめてから駅順に並べる
        // (異なる線区の駅順序を混ぜて比較しても意味が無いため)
        if (isAllRoutes && a.routeDirection !== b.routeDirection) {
          return a.routeDirection.localeCompare(b.routeDirection);
        }
        const seqA = getStationSequence(a, stopsOf(a));
        const seqB = getStationSequence(b, stopsOf(b));
        if (seqA !== seqB) return seqA - seqB;
        // 同順のときは routeDirection → spotId の辞書順で安定ソート
        if (a.routeDirection !== b.routeDirection)
          return a.routeDirection.localeCompare(b.routeDirection);
        return a.spotId.localeCompare(b.spotId);
      }
      // created_at: spotId (UUID v7) の辞書順 = 登録日時順
      return a.spotId.localeCompare(b.spotId);
    });
  }, [filteredPoints, contentFilter, sortOrder, isAllRoutes, routeStops]);

  /** 表示中の行のうちチェックが付いている放送地点 (= 一括削除の対象) */
  const selectedPoints = useMemo(
    () => sortedFilteredPoints.filter((p) => selectedKeys.has(getPointKey(p))),
    [sortedFilteredPoints, selectedKeys],
  );

  // 初期データロード (routes-master.json のみ; 線形 GeoJSON は方面選択後に取得)
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const rm = await fetchRoutesMaster();
        if (aborted) return;
        setRoutes(rm.routes);
      } catch (err) {
        if (!aborted) setErrorMessage(describeApiErrorMessage(err));
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  /**
   * チャイム音マスタ (`GET /chimes`) を初回マウント時に取得する (要件 3・4)。
   *
   * 放送地点フォームのチャイムセレクタの選択肢として使う。取得失敗時は
   * 空配列のままにして画面の他機能に影響を与えない (チャイムはオプション機能)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const res = await chimesClient.list();
        if (!aborted) setChimes(res.items);
      } catch {
        // チャイムマスタの取得失敗は放送地点の編集を妨げないため無視する
        // (セレクタが「(なし)」のみになる)。
        if (!aborted) setChimes([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [chimesClient]);

  /**
   * 定型文マスタ (`GET /phrase-templates?include_defaults=true`) を初回マウント時に取得する
   * (Issue #552)。
   *
   * コンテンツ編集画面の SSML 入力欄の「既定文面 (薄字)」に使う。これが無いと
   * 薄字はコード内定数のままで、**定型文マスタを編集しても画面に反映されない**
   * (「薄字 = 実際に放送される文面」という UI 説明と実挙動が食い違う)。
   *
   * 取得失敗時は空配列のままにして各入力欄をコード内既定文面へフォールバックさせる
   * (チャイム / GTFS stops と同じフェイルセーフ方針。薄字が空欄になる方が
   *  「何が放送されるか分からない」ため実害が大きい)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const res = await phraseTemplatesClient.list(undefined, { includeDefaults: true });
        if (!aborted) setPhraseTemplates(Array.isArray(res.items) ? res.items : []);
      } catch {
        // 定型文マスタの取得失敗は放送地点の編集を妨げないため無視する
        // (薄字がコード内既定文面のままになるだけ)。
        if (!aborted) setPhraseTemplates([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [phraseTemplatesClient]);

  /**
   * 定型文マスタ一覧から作った「既定文面リゾルバ」(Issue #552)。
   *
   * `PhraseTemplateProvider` 経由で各 SSML 入力欄へ配る。
   * 一覧が空 (未取得 / 取得失敗) のときは `undefined` を渡し、
   * 入力欄側でコード内既定文面 (従来の挙動) へフォールバックさせる。
   */
  const phraseTemplateResolver = useMemo(
    () => (phraseTemplates.length > 0 ? buildDefaultSsmlResolver(phraseTemplates) : undefined),
    [phraseTemplates],
  );

  /**
   * 任意テキストテンプレートを 1 回だけ全件取得する。
   * 失敗しても放送地点の編集を妨げないよう、空配列のままにして握り潰す。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const res = await freeTextTemplatesClient.list();
        if (!aborted) setFreeTextTemplates(Array.isArray(res.items) ? res.items : []);
      } catch {
        if (!aborted) setFreeTextTemplates([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [freeTextTemplatesClient]);

  /**
   * 共有テキストを 1 回だけ全件取得する。
   * 失敗しても放送地点の編集を妨げないよう、空配列のままにして握り潰す。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const res = await syncedTextsClient.list();
        if (!aborted) setSyncedTexts(Array.isArray(res.items) ? res.items : []);
      } catch {
        if (!aborted) setSyncedTexts([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [syncedTextsClient]);

  /**
   * GTFS 静的 stops (`GET /gtfs-stops`) を初回マウント時に 1 回だけ全件取得する。
   *
   * 放送条件「停車番線」の駅 / 番線ドロップダウンの候補ソース。`route_id` では
   * 絞り込めない (`stops.txt` に route_id カラムが無い) ため、`routeStops` のように
   * 路線ごとに引くのではなく全件を単発取得してキャッシュする。
   * 取得失敗時は空配列のままにして画面の他機能に影響を与えない
   * (番線セレクトが無効化され、設定済みの値は選択肢として残る)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const stops = await apiClient.getGtfsStops();
        if (!aborted) setGtfsStops(stops);
      } catch {
        // GTFS stops の取得失敗は放送地点の編集を妨げないため無視する
        // (停車番線の番線セレクトが無効化され、注記が表示される)。
        if (!aborted) setGtfsStops([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [apiClient]);

  // routes 読み込み後、全 route_id の駅順マスタを ControlPanel API からまとめて取得する。

  // 接続線遅延コンテンツの開始/終了駅ドロップダウンで利用する。
  // 取得に失敗した route_id は単にスキップする (ドロップダウンは空になるだけ)。
  useEffect(() => {
    let aborted = false;
    if (routes.length === 0) return;
    void (async () => {
      const entries = await Promise.all(
        routes.map(async (route) => {
          try {
            const data = await apiClient.getRouteStops(route.route_id);
            return [route.route_id, sortStopsBySequence(data.stops)] as const;
          } catch {
            // 駅順マスタ未登録 (404) 等は無視する
            return [route.route_id, [] as RouteStop[]] as const;
          }
        }),
      );
      if (aborted) return;
      setRouteStops(Object.fromEntries(entries));
    })();
    return () => {
      aborted = true;
    };
  }, [routes, apiClient]);

  // 選択中の線区/方面に対応する線形 GeoJSON を取得する。
  // - 線区/方面のいずれかが未選択 → null (= 線形を表示しない)
  // - 全線区モード → null (単一の線形を特定できないため)
  // - GeoJSON が存在しない (404 等) → null (= 同上)
  useEffect(() => {
    let aborted = false;
    if (isAllRoutes || !selectedRouteId || !selectedDirectionName) {
      setRouteShapePoints(null);
      return;
    }
    // route/direction 切替時は一旦クリアして表示の取り違えを防ぐ
    setRouteShapePoints(null);
    void (async () => {
      const data = await fetchRouteShapePoints(selectedRouteId, selectedDirectionName);
      if (!aborted) setRouteShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [isAllRoutes, selectedRouteId, selectedDirectionName]);

  // 選択中の線区の路線線形 (LineString) を取得する。
  // 全線区モード / 未選択 / 線形未配置の線区では取得しない (= 線を描かない)。
  // 取得結果は route_id 付きで保持し、線区切替直後に旧線区の線を描かないようにする。
  useEffect(() => {
    if (isAllRoutes || !selectedRouteId) return;
    let aborted = false;
    void (async () => {
      const file = await fetchRouteLine(selectedRouteId);
      if (!aborted) setRouteLine(file ? { routeId: selectedRouteId, file } : null);
    })();
    return () => {
      aborted = true;
    };
  }, [isAllRoutes, selectedRouteId]);

  /** 地図に描く路線線形 (選択中の線区のものだけ) */
  const visibleRouteLine =
    !isAllRoutes && routeLine && routeLine.routeId === selectedRouteId ? routeLine.file : null;
  /** 路線線形の描画色 = 線区カラー (routes-master.json の route_color) */
  const routeLineColor = getRouteColor(routes, selectedRouteId).background;

  /**
   * 一覧取得。
   *
   * 線区が未選択のとき (= トップページの線区選択画面) は API を呼ばない。
   * 従来は全件 scan していたが、その状態で一覧を見る運用が無いため、
   * 初期表示の待ち時間と DynamoDB scan コストを削減する。
   *
   * ただしユーザーが明示的に「(全線区)」を選んだ場合 (= `ALL_ROUTES`) は、
   * `routeDirection` 無しで一覧を取得する (= 全件 scan)。
   * 意図した操作なので scan コストを受け入れる。
   *
   * 線区マスタ未読込 (`routes.length === 0`) のときも呼ばない。
   * `sanitizeSelection` はマスタ未読込時は検証をスキップするため、
   * URL に不正な線区が指定されていると一瞬だけ不正な routeDirection で
   * Query を投げてしまうのを防ぐ。
   */
  const reload = useCallback(async (): Promise<void> => {
    if (routes.length === 0 || !selectedRouteId) {
      // NOTE: 空配列を毎回新規生成して setState すると、`apiClient` (= reload) の
      // 参照が何らかの理由で毎レンダー変わる状況で無限ループになり得る。
      // 既に空なら state を触らないことで安全側に倒す。
      setAllPoints((prev) => (prev.length === 0 ? prev : []));
      setLoading(false);
      setErrorMessage(null);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      // 方面まで選択済みなら Query、線区のみ / 全線区なら scan +
      // (線区のみの場合は) クライアント側で絞り込み
      const res = await apiClient.list(selectedRouteDirection || undefined);
      setAllPoints(res.items);
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [apiClient, routes, selectedRouteId, selectedRouteDirection]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 編集 / 新規作成モーダル表示中は、ヘッダータイトルからのトップ遷移をブロックする。
   * (誤クリックで入力内容が失われるのを防ぐ)
   */
  const { setBlocked } = useHomeNavigationGuard();
  useEffect(() => {
    setBlocked(formState !== null);
    return () => setBlocked(false);
  }, [formState, setBlocked]);

  /**
   * 新規作成モーダルを開く。`latitude` / `longitude` を渡すと、その座標が入った状態で開く
   * (地図の右クリック →「放送地点を新規作成」)。
   */
  const openCreateForm = (latitude?: number, longitude?: number): void => {
    if (!selectedRouteDirection) return;
    setFormState({
      mode: 'create',
      initialValue: {
        routeDirection: selectedRouteDirection,
        spotId: undefined,
        description: '',
        latitude,
        longitude,
        radius_meters: DEFAULT_RADIUS,
        broadcast_delay_seconds: DEFAULT_BROADCAST_DELAY,
        // voice_id 未設定は空文字 = サーバー側デフォルト (Takumi) にフォールバック。
        voice_id: '',
        // Issue #639: 日本語以外の言語別話者。新規作成時は未選択 (= 各言語の既定話者)。
        voice_by_language: {},
        // 要件 3・4: 新規作成時のチャイムは既定で「(なし)」= 鳴らさない (既存挙動と同一)。
        start_chime_id: '',
        end_chime_id: '',
        contents: [],
      },
    });
  };

  /** 「＋ 新規作成」ボタン (座標なし)。onClick のイベント引数が座標に化けないよう引数を捨てる */
  const handleCreate = (): void => openCreateForm();

  /** 地図を右クリック →「放送地点を新規作成」(座標あり) */
  const handleRegisterAtLocation = (lat: number, lng: number): void => openCreateForm(lat, lng);

  /**
   * 地図の右クリックメニューが使えない理由。使える場合は undefined。
   * 新規作成ボタンと同じ条件 (登録先の線区 + 方面が決まっていること)。
   */
  const registerDisabledReason = selectedRouteDirection
    ? undefined
    : isAllRoutes
      ? '全線区表示中は登録できません。線区と方面を選択してください'
      : '登録するには方面を選択してください';

  /**
   * 放送地点 1 件から、編集モーダルの初期値 (フォーム値) を組み立てる。
   * 編集 (`handleEdit`) と複製 (`handleCopyConfirm`) で共通利用する。
   */
  const buildInitialValue = (point: BroadcastPoint): BroadcastPointFormValues => ({
    routeDirection: point.routeDirection,
    spotId: point.spotId,
    description: point.description ?? '',
    latitude: point.latitude,
    longitude: point.longitude,
    radius_meters: point.radius_meters ?? DEFAULT_RADIUS,
    broadcast_delay_seconds: point.broadcast_delay_seconds ?? DEFAULT_BROADCAST_DELAY,
    // voice_id も同様に、DynamoDB 上で未設定なら属性欠落。空文字を初期値としてセットする
    // (保存時に handleFormSubmit で空文字→undefined に正規化)。
    // 日本語の話者はトップレベル voice_id。無ければ voice_settings.ja で補う (API 経由で設定されたデータ用)。
    voice_id: point.voice_id ?? japaneseVoiceFromSettings(point.voice_settings),
    // Issue #639: 日本語以外の言語別話者 (voice_settings → フォーム用辞書)。
    voice_by_language: toVoiceSelections(point.voice_settings),
    // 要件 3・4: チャイム設定も DynamoDB 上で未設定なら属性欠落のため空文字で初期化する
    // (保存時に handleFormSubmit で空文字→undefined に正規化)。
    start_chime_id: point.start_chime_id ?? '',
    end_chime_id: point.end_chime_id ?? '',
    // Issue #679: 無効状態を保存 (PUT = 全置換) で失わないよう引き継ぐ。
    ...(point.enabled === false ? { enabled: false } : {}),
    contents: extractContents(point),
  });

  const handleEdit = (point: BroadcastPoint): void => {
    setFormState({ mode: 'edit', initialValue: buildInitialValue(point) });
  };

  /**
   * 変更通知メールのリンク (`?route=...&direction=...&edit=<spotId>`) から開かれたとき、
   * 該当の放送地点を **編集モードで自動的に開く** (Issue #806)。
   *
   * - `edit` は一覧を取得できてから探す。見つからなければ (削除済み等) 何も開かない。
   * - 開いたら (見つからなくても) `edit` をクエリから外し、保存後の再描画や戻る操作で
   *   モーダルが再び開くのを防ぐ。線区 / 方面のクエリはそのまま残す。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const editSpotId = searchParams.get(EDIT_QUERY_KEY);
  useEffect(() => {
    if (!editSpotId || loading || routes.length === 0 || !selectedRouteId) return;
    const target = allPoints.find((p) => p.spotId === editSpotId);
    if (target) {
      setFormState({ mode: 'edit', initialValue: buildInitialValue(target) });
    }
    // 一覧の取得が終わっていない間は何もしない (取得前に外すと開けなくなる)。
    if (target || (!loading && allPoints.length > 0)) {
      const next = new URLSearchParams(searchParams);
      next.delete(EDIT_QUERY_KEY);
      setSearchParams(next, { replace: true });
    }
    // buildInitialValue は毎回生成される関数で、依存に入れると毎レンダー実行されてしまう。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSpotId, loading, routes, selectedRouteId, allPoints]);

  /**
   * 編集の更新確定前に、開いてから他者がこの地点を更新 / 削除していないかを確認する (Issue #765)。
   *
   * 放送地点は更新日時 (LastUpdate) を持たないため、開いたときの内容と、サーバから取り直した最新の内容を
   * 同じ変換 (`buildInitialValue`) に通して比べる。最新の地点が無ければ他者が削除した。
   */
  const checkRemoteState = async (): Promise<RemoteChangeState> => {
    const opened = formState?.initialValue;
    if (!opened?.spotId) return 'unchanged';
    const res = await apiClient.list(opened.routeDirection);
    const latest = res.items.find((p) => p.spotId === opened.spotId);
    if (!latest) return 'deleted';
    return fingerprint(buildInitialValue(latest)) === fingerprint(opened)
      ? 'unchanged'
      : 'modified';
  };

  /** 編集中に他者が削除していた: お知らせを閉じた後、モーダルを閉じて一覧を取り直す (Issue #765)。 */
  const handleDeletedByOthers = (): void => {
    setFormState(null);
    void reload();
  };

  /**
   * 一覧の「複製」ボタン押下時のハンドラ。複製元を保持してコピー先選択ダイアログを開く。
   */
  const handleCopy = (point: BroadcastPoint): void => {
    setCopyTarget(point);
  };

  /**
   * コピー先選択ダイアログで「コピーして新規作成」が確定された時のハンドラ。
   *
   * 以下を初期値とする新規作成モーダルを開く:
   *   - routeDirection: ユーザーが選択したコピー先 (元と異なる線区/方面も指定可)
   *   - 半径 / 放送遅延 / コンテンツ ほか: 複製元の値をそのまま引き継ぐ
   *   - spotId: undefined (新規採番させる)
   *   - 説明 / 緯度 / 経度: 空欄にしてユーザーに再入力させる
   *     (同一場所での重複登録を避けるため + 説明はコピー先の地点で書き直す前提)
   */
  const handleCopyConfirm = (routeDirection: string): void => {
    const source = copyTarget;
    setCopyTarget(null);
    if (!source) return;
    setFormState({
      mode: 'create',
      initialValue: {
        ...buildInitialValue(source),
        routeDirection,
        spotId: undefined,
        description: '',
        latitude: undefined,
        longitude: undefined,
      },
    });
  };

  /**
   * 削除ボタン押下時に呼ばれるハンドラ。
   * 確認ダイアログを表示するため対象を state に格納するだけで、実削除は
   * `executeDelete` (= ConfirmDialog の onConfirm) で行う。
   */
  const handleDelete = (point: BroadcastPoint): void => {
    setDeleteTarget(point);
  };

  /**
   * 削除確認ダイアログで OK が押されたときの実削除処理。
   */
  const executeDelete = async (): Promise<void> => {
    const point = deleteTarget;
    setDeleteTarget(null);
    if (!point) return;
    try {
      reportHistory(await apiClient.remove(point.spotId, point.routeDirection));
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
        await reload();
        return;
      }
      setErrorMessage(formatApiError(err));
    }
  };

  /** 行のチェックボックス切替 */
  const handleToggleSelect = (point: BroadcastPoint): void => {
    const key = getPointKey(point);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /** ヘッダのチェックボックス: 表示中の全行を選択 / 解除する */
  const handleToggleSelectAll = (checked: boolean): void => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const p of sortedFilteredPoints) {
        if (checked) next.add(getPointKey(p));
        else next.delete(getPointKey(p));
      }
      return next;
    });
  };

  /**
   * 一括削除の実処理 (確認ダイアログの OK)。
   *
   * 1 件の失敗で残りを止めないよう逐次実行し、失敗分だけを選択状態に残して
   * エラー表示する (再試行しやすくするため)。成功分は一覧を再取得して反映する。
   */
  const executeBulkDelete = async (): Promise<void> => {
    const targets = selectedPoints;
    setBulkDeleteOpen(false);
    if (targets.length === 0) return;
    setBulkDeleting(true);
    const failures: { point: BroadcastPoint; message: string }[] = [];
    const historyResults: HistoryRecordResult[] = [];
    try {
      for (const point of targets) {
        try {
          historyResults.push(await apiClient.remove(point.spotId, point.routeDirection));
        } catch (err) {
          failures.push({ point, message: formatApiError(err) });
        }
      }
      const failedKeys = new Set(failures.map((f) => getPointKey(f.point)));
      setSelectedKeys((prev) => {
        const next = new Set<string>();
        for (const key of prev) {
          if (failedKeys.has(key)) next.add(key);
        }
        return next;
      });
      // 削除に成功した分の履歴登録結果をまとめて警告する (削除の失敗は下のエラー表示)
      reportHistory(...historyResults);
      await reload();
      if (failures.length > 0) {
        const detail = failures
          .map((f) => `${f.point.description || '(名称未設定)'}: ${f.message}`)
          .join(' / ');
        setErrorMessage(
          `${targets.length} 件中 ${failures.length} 件の削除に失敗しました。${detail}`,
        );
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  /**
   * 放送地点の有効 / 無効を切り替える共通処理 (Issue #679)。
   *
   * `POST /broadcast-points/bulk-enabled` で `enabled` だけを部分更新する
   * (PUT は全置換のため使わない)。成功 / 失敗にかかわらず一覧を再取得して
   * サーバーの状態に表示を揃え、更新できなかった地点があればエラー表示する。
   */
  const applyEnabled = async (targets: BroadcastPoint[], enabled: boolean): Promise<void> => {
    if (targets.length === 0) return;
    const keys = targets.map((p) => getPointKey(p));
    setTogglingKeys((prev) => new Set([...prev, ...keys]));
    setErrorMessage(null);
    // `reload()` は冒頭でエラー表示をクリアするため、失敗メッセージは再取得の後で表示する。
    let failureMessage: string | null = null;
    try {
      const result = await apiClient.setEnabled(
        targets.map((p) => ({ routeDirection: p.routeDirection, spotId: p.spotId })),
        enabled,
      );
      reportHistory(result);
      const problems = result.failed.length + result.not_found.length;
      if (problems > 0) {
        failureMessage = `${targets.length} 件中 ${problems} 件の${enabled ? '有効化' : '無効化'}に失敗しました。`;
      }
    } catch (err) {
      failureMessage = formatApiError(err);
    }
    try {
      await reload();
    } finally {
      setTogglingKeys((prev) => {
        const next = new Set(prev);
        for (const key of keys) next.delete(key);
        return next;
      });
    }
    if (failureMessage) setErrorMessage(failureMessage);
  };

  /** 一覧の行トグル: 1 件の有効 / 無効を切り替える */
  const handleToggleEnabled = (point: BroadcastPoint, enabled: boolean): void => {
    void applyEnabled([point], enabled);
  };

  /** 選択中の地点をまとめて有効 / 無効にする (一括設定) */
  const handleBulkSetEnabled = async (enabled: boolean): Promise<void> => {
    if (selectedPoints.length === 0) return;
    setBulkTogglingEnabled(true);
    try {
      await applyEnabled(selectedPoints, enabled);
    } finally {
      setBulkTogglingEnabled(false);
    }
  };

  const handleFormSubmit = async (values: BroadcastPointFormValues): Promise<void> => {
    // voice_id も同じく空文字は未設定扱い (= サーバー側デフォルト Takumi にフォールバック)。
    const voiceIdTrimmed = (values.voice_id ?? '').trim();
    // Issue #639: 言語別話者。放送言語 (コンテンツの languages の和集合) に含まれない言語の選択は保存しない。
    // 選択が無ければ undefined (= 属性を作らない = 既存 item と同じ形)。
    const voiceSettings = buildVoiceSettingsPayload(
      values.voice_by_language,
      collectContentLanguages(values.contents),
    );
    // 要件 3・4: 放送開始 / 終了チャイム。
    // 「(なし)」選択 = 空文字は「鳴らさない」を意味する。
    //   - 新規作成時: 属性そのものを送らない (undefined) ことで DynamoDB に保存しない。
    //   - 更新時: 空文字を送ると Lambda 側 (_coerce_chime_id) が item から除去するため、
    //     PUT が put_item (全置換) である性質と合わせて設定解除として機能する。
    // どちらのケースも `undefined` でよい (更新は全置換なので属性が消える)。
    const startChimeTrimmed = (values.start_chime_id ?? '').trim();
    const endChimeTrimmed = (values.end_chime_id ?? '').trim();
    const payload = {
      routeDirection: values.routeDirection,
      description: values.description,
      latitude: values.latitude,
      longitude: values.longitude,
      radius_meters: values.radius_meters,
      broadcast_delay_seconds: values.broadcast_delay_seconds,
      voice_id: voiceIdTrimmed === '' ? undefined : voiceIdTrimmed,
      voice_settings: voiceSettings,
      start_chime_id: startChimeTrimmed === '' ? undefined : startChimeTrimmed,
      end_chime_id: endChimeTrimmed === '' ? undefined : endChimeTrimmed,
      // Issue #679: 無効 (false) のときだけ送る (未指定 = 有効)。
      enabled: values.enabled === false ? false : undefined,
      contents: values.contents,
    };
    // モーダルはここで閉じるので、履歴の警告はモーダルではなくこのページ側に出す。
    // レスポンスの historyRecordSucceeded は警告に使うだけで、フォーム状態や次のリクエストには混ぜない。
    if (formState?.mode === 'edit' && values.spotId) {
      reportHistory(await apiClient.update(values.spotId, payload));
    } else {
      reportHistory(await apiClient.create(payload));
    }
    setFormState(null);
    await reload();
  };

  /**
   * Import 1 件分の API payload を組み立てる。
   *
   * 空文字は「未設定」に正規化する (parseImportJson で trim 空は undefined に
   * なっているはずだが、JSON を手編集された場合に備えた二重防御)。
   */
  const buildImportPayload = (
    item: BroadcastPoint,
  ): Omit<BroadcastPoint, 'spotId'> & { spotId?: string } => {
    const voiceIdTrimmed = (item.voice_id ?? '').trim();
    // 要件 3・4: チャイム設定も空文字は未設定扱い (鳴らさない) に正規化する。
    const startChimeTrimmed = (item.start_chime_id ?? '').trim();
    const endChimeTrimmed = (item.end_chime_id ?? '').trim();
    return {
      routeDirection: item.routeDirection,
      description: item.description,
      latitude: item.latitude,
      longitude: item.longitude,
      radius_meters: item.radius_meters,
      broadcast_delay_seconds: item.broadcast_delay_seconds,
      voice_id: voiceIdTrimmed === '' ? undefined : voiceIdTrimmed,
      // Issue #639: Import JSON の言語別話者。不正な話者は落として保存時 400 を防ぐ。
      voice_settings: buildVoiceSettingsPayload(
        toVoiceSelections(item.voice_settings),
        collectContentLanguages(item.contents),
      ),
      start_chime_id: startChimeTrimmed === '' ? undefined : startChimeTrimmed,
      end_chime_id: endChimeTrimmed === '' ? undefined : endChimeTrimmed,
      enabled: item.enabled === false ? false : undefined,
      contents: item.contents,
    };
  };

  /**
   * 確認ダイアログ用の事前サマリ (更新 / 新規の見込み件数)。
   *
   * 判定は「読み込み済み一覧 (`allPoints`) に同じ複合キーがあるか」で行うため厳密では
   * ない (Import JSON に一覧対象外の方面が混ざっていると新規側に数える)。実行時は
   * 404 / 409 のフォールバックで正しい側へ振り直されるので、目安表示として扱う。
   */
  const summarizeImport = (
    importItems: BroadcastPoint[],
  ): { updateCount: number; createCount: number } => {
    const existing = buildExistingSpotKeySet(allPoints);
    let updateCount = 0;
    for (const item of importItems) {
      if (item.spotId && existing.has(spotCompositeKey(item.routeDirection, item.spotId))) {
        updateCount++;
      }
    }
    return { updateCount, createCount: importItems.length - updateCount };
  };

  /**
   * JSON Import の一括 upsert。
   *
   * **spotId が Import 先に存在すれば更新 (PUT)、存在しなければ新規作成 (POST)** する。
   * ControlPanel API の PUT は更新専用で、該当アイテムが無いと 404 を返すため
   * (`ConditionExpression=Attr("spotId").exists()`)、読み込み済み一覧と照合して
   * PUT / POST を振り分ける。これにより
   *   - Import 先が 0 件 (別環境への全件移送) → 全件 POST が 1 回ずつ
   *   - 同一環境への上書き Import          → 全件 PUT が 1 回ずつ
   * となり、無駄なリクエストが発生しない。
   *
   * 事前判定が外れる場合 (一覧に載っていない方面の地点が混ざっている等) の安全網として、
   * 404 (更新対象なし) は POST へ、409 (既に存在) は PUT へ **1 回だけ** 振り直す。
   * POST には元の `spotId` をそのまま渡すため **環境間で spotId が保持される**
   * (API は body の spotId を尊重し、未指定時のみ UUIDv7 を採番する)。
   *
   * 件数の上限は無い。1 件 = 1 リクエストの直列実行なので API Gateway / Lambda の
   * タイムアウト (29 / 30 秒) は 1 件ずつにしか掛からず、DynamoDB もオンデマンドの
   * 単発 put_item (BatchWriteItem の 25 件制限は使っていない)。件数が多いと時間が
   * かかるだけなので、`onProgress` で進捗を通知して画面に表示する。
   */
  const handleImport = async (
    importItems: BroadcastPoint[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<{ created: number; updated: number; failures: string[] }> => {
    const existing = buildExistingSpotKeySet(allPoints);
    let created = 0;
    let updated = 0;
    const failures: string[] = [];
    const historyResults: HistoryRecordResult[] = [];
    let done = 0;

    for (const item of importItems) {
      const payload = buildImportPayload(item);
      const spotId = item.spotId;
      const shouldUpdate =
        Boolean(spotId) && existing.has(spotCompositeKey(item.routeDirection, spotId));

      try {
        if (shouldUpdate && spotId) {
          try {
            historyResults.push(await apiClient.update(spotId, payload));
            updated++;
          } catch (err) {
            // 一覧が古い等で対象が消えていた場合は新規作成へ振り直す (spotId は維持)。
            if (err instanceof ApiError && err.status === 404) {
              historyResults.push(await apiClient.create({ ...payload, spotId }));
              created++;
            } else {
              throw err;
            }
          }
        } else {
          try {
            // spotId が空文字の場合はキーを送らず、API 側で UUIDv7 を採番させる。
            historyResults.push(await apiClient.create(spotId ? { ...payload, spotId } : payload));
            created++;
          } catch (err) {
            // 一覧に載っていない方面の既存地点だった場合は更新へ振り直す。
            if (err instanceof ApiError && err.status === 409 && spotId) {
              historyResults.push(await apiClient.update(spotId, payload));
              updated++;
            } else {
              throw err;
            }
          }
        }
      } catch (err) {
        failures.push(`${item.routeDirection}/${spotId || '(new)'}: ${formatApiError(err)}`);
      } finally {
        done++;
        onProgress?.(done, importItems.length);
      }
    }

    reportHistory(...historyResults);
    await reload();
    return { created, updated, failures };
  };

  /**
   * 地図を表示するか。
   * 線区が選択されている (全線区モードも含む) ときに表示する。
   * 全線区モードでは線形ラインは描かず、放送地点ピンのみを重ねて表示する。
   */
  const showOverviewMap = selectedRouteId !== '';

  /**
   * 地図セクション (SplitPane の右ペインに配置する)。
   * 線区が選択されているときのみ描画される。
   */
  const mapSection = (
    <section className="section points-layout__map" data-testid="map-section">
      <div className="overview-map">
        <BroadcastPointsMap
          otherPoints={filteredPoints}
          routeShapePoints={routeShapePoints}
          onPointClick={handleEdit}
          focusPoint={focusPoint}
          // 地図右上の「駅へ移動」ドロップダウン用。全線区モードでは線区を特定できないため渡さない。
          stations={isAllRoutes ? undefined : routeStops[selectedRouteId]}
          // 路線の線形を線区カラーで描く (線形データがある線区のみ。例: 横浜線)。
          routeLine={visibleRouteLine}
          routeLineColor={routeLineColor}
          // 線区 / 方面を開いたときは、新規作成時と同様に路線全体が収まる縮尺で表示する。
          // 全線区モードは対象路線を特定できないため従来どおり。
          fitToRoute={!isAllRoutes}
          fitResetKey={`${selectedRouteId}|${selectedDirectionName}`}
          // 地図を右クリック →「放送地点を新規作成」。地図の外 (一覧など) は標準メニューのまま。
          onRegisterAtLocation={handleRegisterAtLocation}
          registerDisabledReason={registerDisabledReason}
        />
      </div>
    </section>
  );

  /**
   * 放送地点一覧セクション (SplitPane の左ペインに配置する)。
   * 線区未選択で地図が無い場合は、このセクションのみ全幅で表示する。
   */
  const listSection = (
    <section className="section points-layout__list" data-testid="list-section">
      <h2>
        放送地点一覧{' '}
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : sortedFilteredPoints.length === filteredPoints.length ? (
          `(${filteredPoints.length} 件)`
        ) : (
          `(${sortedFilteredPoints.length} / ${filteredPoints.length} 件)`
        )}
      </h2>
      {historyWarning}
      {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
      <div className="sort-filter-bar">
        <div className="sort-filter-group">
          <span className="sort-filter-label">ソート</span>
          <label className="sort-filter-radio">
            <input
              type="radio"
              name="sortOrder"
              value="station_order"
              checked={sortOrder === 'station_order'}
              onChange={() => setSortOrder('station_order')}
            />
            駅並び順
          </label>
          <label className="sort-filter-radio">
            <input
              type="radio"
              name="sortOrder"
              value="created_at"
              checked={sortOrder === 'created_at'}
              onChange={() => setSortOrder('created_at')}
            />
            登録日時順
          </label>
        </div>
        <div className="sort-filter-group">
          <label className="sort-filter-select-label" htmlFor="content-filter">
            <span className="sort-filter-label">コンテンツ種別</span>
            <select
              id="content-filter"
              value={contentFilter}
              onChange={(e) => setContentFilter(e.target.value as ContentFilterValue)}
            >
              <option value="">すべて</option>
              <option value="self_train_delay">自列車遅延</option>
              <option value="connecting_line_delay">他線区遅延</option>
              <option value="train_info">運行情報</option>
              <option value="free_text">任意テキスト</option>
            </select>
          </label>
        </div>
      </div>
      <div className="bulk-actions" data-testid="bulk-actions">
        <span data-testid="selected-count">選択中: {selectedPoints.length} 件</span>
        <button
          type="button"
          data-testid="bulk-enable-button"
          disabled={selectedPoints.length === 0 || bulkDeleting || bulkTogglingEnabled}
          onClick={() => setBulkEnabledConfirm(true)}
        >
          選択した地点を一括有効
        </button>
        <button
          type="button"
          data-testid="bulk-disable-button"
          disabled={selectedPoints.length === 0 || bulkDeleting || bulkTogglingEnabled}
          onClick={() => setBulkEnabledConfirm(false)}
        >
          選択した地点を一括無効
        </button>
        <button
          type="button"
          className="danger"
          data-testid="bulk-delete-button"
          disabled={selectedPoints.length === 0 || bulkDeleting}
          onClick={() => setBulkDeleteOpen(true)}
        >
          {bulkDeleting ? (
            <>
              <Spinner />
              削除中...
            </>
          ) : (
            '選択した地点を一括削除'
          )}
        </button>
      </div>
      <BroadcastPointsTable
        points={sortedFilteredPoints}
        onEdit={handleEdit}
        onCopy={handleCopy}
        onDelete={handleDelete}
        selectedKeys={selectedKeys}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        selectionDisabled={bulkDeleting}
        onToggleEnabled={handleToggleEnabled}
        togglingKeys={togglingKeys}
        onRowClick={showOverviewMap ? setFocusPoint : undefined}
      />
    </section>
  );

  /**
   * 左カラム: 操作パネル (線区/方面セレクタ + アクション) + 放送地点一覧。
   *
   * 画面全体を活かすため、操作パネルは上部に固定し、一覧テーブルだけを
   * 残り領域でスクロールさせる (`.points-layout` / `.points-layout__list`)。
   */
  const leftColumn = (
    <div className="points-layout">
      {/*
        操作パネル。
        以前は「放送新規設定」という見出しを付けていたが、意味が伝わらないため削除した。
        また「選択中の線区/方面バッジ」も廃止した (線区/方面ドロップダウンの現在値と
        同じ情報が 2 箇所に出て冗長だったため)。線区カラーは RouteSelector 内で
        ドロップダウン下の路線ライン + パネル左端のアクセントラインとして表示する。
      */}
      <section className="section points-layout__controls">
        <RouteSelector
          routes={routes}
          selectedRouteId={selectedRouteId}
          selectedDirectionName={selectedDirectionName}
          onChange={(nextRouteId, nextDirectionName) => {
            setSelection(nextRouteId, nextDirectionName);
          }}
          onCreate={handleCreate}
          onReload={() => void reload()}
          onBackToTop={clearSelection}
          extraActions={
            <ImportExportControl
              items={filteredPoints}
              onImport={handleImport}
              summarizeImport={summarizeImport}
            />
          }
        />
      </section>
      {listSection}
    </div>
  );

  /**
   * 線区未選択 (= トップページ) のときは、テーブルの代わりに線区選択ランディングを表示する。
   * ランディングは 1 段階選択なので、方面ボタン 1 クリックで `setSelection` により
   * 選択が確定し、そのまま一覧 + 地図画面へ遷移する。
   *
   * NOTE: 判定は「空文字かどうか」で行う。`ALL_ROUTES` (= 全線区) は
   * ユーザーが明示的に選んだ状態なのでランディングへ戻してはいけない。
   */
  if (selectedRouteId === '') {
    return (
      <>
        <RouteLandingSelector
          routes={routes}
          loading={routes.length === 0 && errorMessage === null}
          onSelect={(nextRouteId, nextDirectionName) =>
            setSelection(nextRouteId, nextDirectionName)
          }
        />
        {errorMessage ? (
          <div className="error-message" style={{ padding: '0 24px 16px' }}>
            {errorMessage}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      {/*
        受け入れ条件「コンテンツ一覧と地図が横並びで表示されること」に対応して、
        左カラム (操作パネル + 一覧) と地図セクションを `SplitPane` で横並びにする。
        分割バーはマウスドラッグ (またはキーボード ←→) で領域配分を変更できる。

        `fullHeight` により SplitPane はメイン領域 (ヘッダー以下すべて) の高さを
        占め、地図はウィンドウ下端まで広がる。

        線区が未選択のときは上の early return でランディング画面を返すため、
        ここでは常に地図付きの 2 カラムになる (showOverviewMap は防御的判定)。
      */}
      {showOverviewMap ? (
        <SplitPane
          testId="points-map-split"
          // 一覧側 (左) を少し広めにして、地図 (右) と並べて見比べやすくする
          initialLeftRatio={55}
          fullHeight
          splitterAriaLabel="一覧と地図の領域配分を変更"
          left={leftColumn}
          right={mapSection}
        />
      ) : (
        leftColumn
      )}

      {formState ? (
        /*
          Issue #492 (SBI-11): 多言語入力欄の「日本語から翻訳」ボタン (Amazon Translate)。

          翻訳ボタンは `LocalizedTextFields` (6 階層下 / 5 箇所の Fields から利用) に
          置くため、ハンドラは props リレーではなくコンテキストで供給する
          (1 箇所渡し忘れると「その欄だけボタンが出ない」欠落になるため)。

          ⚠️ ここで渡すのは「訳文を取得する」処理だけ。**保存は一切行わない**。
          サーバ (`POST /translate`) も保存しないので、機械翻訳の訳文は
          入力欄の下書きに留まり、実際の保存は下の `onSubmit` (保存ボタン) で
          運用者が明示的に行う。誤案内防止のため、この前提を崩さないこと。
        */
        /*
          Issue #552: 定型文マスタの既定文面を SSML 入力欄へ配る。

          各 SSML 欄の薄字 (placeholder) と多言語欄の既定値を
          **定型文マスタの文面** にすることで、
          「SSML 欄が空欄なら定型文マスタの文面が放送される (薄字で表示)」という
          画面上の説明どおりの挙動になる。マスタ未取得 / 取得失敗のときは
          `undefined` が配られ、各欄はコード内既定文面 (従来の挙動) を表示する。

          ⚠️ 既定文面は **表示専用**。入力欄の値として焼き込むと個別文面が
          マスタより優先され、「マスタ編集で空欄コンテンツの放送内容が一括で変わる」
          (Issue #527 の受け入れ条件) が壊れる。
        */
        <PhraseTemplateProvider resolveDefault={phraseTemplateResolver}>
          {/* 任意テキスト放送の「登録済みテキストから選択」の候補 (取得失敗時は空で選択 UI を出さない) */}
          <FreeTextTemplateProvider templates={freeTextTemplates}>
            <SyncedTextProvider texts={syncedTexts}>
              <TranslateProvider
                translate={async ({ sourceText, targetLanguages }) => {
                  const response = await apiClient.translate({ sourceText, targetLanguages });
                  return { translations: response.translations, failed: response.failed };
                }}
              >
                <BroadcastPointForm
                  mode={formState.mode}
                  initialValue={formState.initialValue}
                  otherPoints={filteredPoints.filter(
                    (p) => p.spotId !== formState.initialValue.spotId,
                  )}
                  routeShapePoints={routeShapePoints}
                  // 親が `routeShapePoints` をどの routeDirection 用に取得したかを明示する。
                  // 一覧の「複製」でコピー元と異なる線区/方面が選ばれた際、
                  // フォーム側でこの値と `initialValue.routeDirection` のズレを検知し、
                  // 親値の代わりにフォーム側で自前 fetch した線形を使って地図を描画させる。
                  routeShapePointsRouteDirection={selectedRouteDirection}
                  routes={routes}
                  routeStops={routeStops}
                  // 接続線遅延の「運行情報の参照元（アイステイションズ路線）」複数選択用マスタ。
                  // ATOS 線区マスタ (routes) とは独立した別系統で、連動させない。
                  istationsLines={istationsLines}
                  // 任意テキストの放送条件「停車番線」の番線ドロップダウン候補 (GTFS stops 全件)。
                  // route_id で絞り込めないため、マウント時に 1 回だけ取得した全件を渡す。
                  gtfsStops={gtfsStops}
                  // 要件 3・4: 放送開始 / 終了チャイムの選択肢 (チャイムマスタ) と試聴ハンドラ。
                  chimes={chimes}
                  onPreviewChime={async (chimeId) => {
                    const { content_type, content_base64 } = await chimesClient.getAudio(chimeId);
                    return base64ToAudioUrl(content_base64, content_type);
                  }}
                  onUploadAudio={async (file) => {
                    const { audio_key } = await apiClient.uploadAudio(file);
                    return audio_key;
                  }}
                  onPreviewSsml={async (input) => {
                    const response = await apiClient.previewAudio({
                      text: input.text,
                      ssml: input.ssml,
                      isSsml: input.isSsml,
                      // モーダル内で選択された Polly VoiceId (未選択は undefined) をそのまま
                      // プレビュー API に渡すことで、実放送と同じ声色で試聴できるようにする。
                      voiceId: input.voiceId,
                      language: input.language,
                      // Issue #439: 放送コンテンツをまるごと渡すと、サーバ側が UpdateContents と
                      // 同じ `ssml_builder` で実放送と同一の文面を組み立ててから合成する。
                      // これにより「任意テキストの放送」以外 (自列車遅延 / 接続線遅延 /
                      // 接続線遅延(乗換駅別) / 音声ファイル) もプレビューできる。
                      content: input.content,
                      sampleContext: input.sampleContext,
                    });
                    return {
                      url: base64ToAudioUrl(response.content_base64, response.content_type),
                      // 診断情報 (`content` 指定時のみサーバが返す) をそのまま UI へ渡し、
                      // 「読み上げた文面」「採用されたパターン行」「未展開トークン警告」を表示させる。
                      renderedSsml: response.rendered_ssml,
                      // 辞書適用後の SSML (辞書が効いたときのみ)。「読み上げた文面」表示に使う。
                      appliedSsml: response.applied_ssml,
                      matchedPatternIndex: response.matched_pattern_index,
                      unresolvedTokens: response.unresolved_tokens,
                    };
                  }}
                  onSubmit={handleFormSubmit}
                  onCheckRemoteState={checkRemoteState}
                  onDeletedByOthers={handleDeletedByOthers}
                  onClose={() => setFormState(null)}
                />
              </TranslateProvider>
            </SyncedTextProvider>
          </FreeTextTemplateProvider>
        </PhraseTemplateProvider>
      ) : null}
      {/* 複製のコピー先 (線区/方面) 選択ダイアログ */}
      {copyTarget ? (
        <CopyDialog
          routes={routes}
          sourceRouteDirection={copyTarget.routeDirection}
          onClose={() => setCopyTarget(null)}
          onConfirm={handleCopyConfirm}
        />
      ) : null}
      {/* 削除確認ダイアログ (window.confirm の代替) */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="放送地点の削除"
        message={`放送地点 "${deleteTarget?.description ?? '(名称未設定)'}" を削除します。よろしいですか？`}
        confirmLabel="削除"
        destructive
        onConfirm={() => void executeDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
      {/* 一括有効 / 無効の確認ダイアログ (Issue #765) */}
      <ConfirmDialog
        open={bulkEnabledConfirm !== null}
        title={bulkEnabledConfirm === false ? '放送地点の一括無効' : '放送地点の一括有効'}
        message={`選択した放送地点を全て${bulkEnabledConfirm === false ? '無効' : '有効'}にします。よろしいでしょうか。`}
        confirmLabel={bulkEnabledConfirm === false ? '無効にする' : '有効にする'}
        onConfirm={() => {
          const enabled = bulkEnabledConfirm;
          setBulkEnabledConfirm(null);
          if (enabled !== null) void handleBulkSetEnabled(enabled);
        }}
        onCancel={() => setBulkEnabledConfirm(null)}
      />
      {/* 一括削除確認ダイアログ (Issue #628) */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="放送地点の一括削除"
        message={`選択した ${selectedPoints.length} 件の放送地点を削除します。この操作は元に戻せません。よろしいですか？`}
        confirmLabel="削除"
        destructive
        onConfirm={() => void executeBulkDelete()}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </>
  );
}

/** API クライアントの例外を画面表示用の日本語メッセージに整形する (Issue #815) */
function formatApiError(err: unknown): string {
  return describeApiErrorMessage(err);
}

/**
 * 放送地点の座標から「最近傍区間の開始駅 stop_sequence」を返す。
 * 駅順マスタが空、または座標が未設定の場合は Infinity を返して末尾に送る。
 */
function getStationSequence(point: BroadcastPoint, stops: RouteStop[]): number {
  if (
    stops.length < 2 ||
    typeof point.latitude !== 'number' ||
    typeof point.longitude !== 'number'
  ) {
    return Infinity;
  }
  const segment = findStationSegment(stops, point.latitude, point.longitude);
  return segment?.startStop.stop_sequence ?? Infinity;
}

/**
 * BroadcastSpots テーブルの複合キー (PK=routeDirection / SK=spotId) を
 * 1 本の文字列に畳んだ照合用キーを返す。
 *
 * 区切りには `routeDirection` / `spotId` のどちらにも現れない `\u0000` を使う。
 * `#` は `routeDirection` 自体が含む文字 (例 `"京浜#北行"`) なので使えない。
 */
function spotCompositeKey(routeDirection: string, spotId: string | undefined): string {
  return `${routeDirection}\u0000${spotId ?? ''}`;
}

/**
 * Import 先に既に存在する放送地点の複合キー集合を作る。
 *
 * JSON Import の upsert 判定 (PUT か POST か) に使う。判定対象は
 * **読み込み済みの一覧 (`allPoints`)** なので、一覧に含まれない線区/方面の地点は
 * 「存在しない」と判定される。誤判定時は 404 / 409 のフォールバックが救済する。
 */
function buildExistingSpotKeySet(points: BroadcastPoint[]): Set<string> {
  return new Set(points.map((p) => spotCompositeKey(p.routeDirection, p.spotId)));
}
